/**
 * UploadField — the single premium upload surface for every creation builder.
 *
 * A controlled component with an explicit state machine the caller drives via
 * `status`: "idle" → "uploading" → "done" / "error". The caller keeps its own
 * upload mechanic (decoupled URL upload, multipart, per-stem, …) and simply
 * reflects state here, so all three builders render an identical, premium surface.
 *
 * Features:
 *  • Real drag & drop with a distinct drag-over state.
 *  • Real previews — image thumbnail, auto-captured VIDEO poster frame, or a
 *    typed placeholder; remote `previewUrl` always wins over the local file.
 *  • Determinate upload ring + percentage, success check, inline error + Retry.
 *  • File meta chip (name · size · duration) and hover Replace / Remove actions.
 *  • Client-side type/size validation with inline (not toast) feedback.
 *
 * Integration is controlled-only by design — pass `status`/`progress`/`previewUrl`
 * and handle `onSelect(file)` / `onRemove()` / `onRetry()`.
 */
import React, { useRef, useState, useEffect, useCallback } from "react";
import { ENV } from "config/environment";
import { Box, Stack, Typography, CircularProgress } from "@mui/material";
import ImageIcon from "@mui/icons-material/Image";
import VideocamIcon from "@mui/icons-material/Videocam";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

const ACCENTS = {
  image: "#22d3ee",
  video: "#ec4899",
  audio: "#d946ef",
};

const ICONS = {
  image: ImageIcon,
  video: VideocamIcon,
  audio: AudiotrackIcon,
};

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatClock(sec) {
  if (!sec || sec < 0) return "";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

// Capture the first frame of a video File as a poster data-URL.
function captureVideoPoster(file) {
  return new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(file);
      const video = document.createElement("video");
      video.preload = "metadata";
      video.muted = true;
      video.src = url;
      const cleanup = () => URL.revokeObjectURL(url);
      video.onloadeddata = () => {
        try {
          video.currentTime = Math.min(0.1, video.duration || 0.1);
        } catch {
          cleanup();
          resolve(null);
        }
      };
      video.onseeked = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = video.videoWidth || 640;
          canvas.height = video.videoHeight || 360;
          canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
          const data = canvas.toDataURL("image/jpeg", 0.7);
          cleanup();
          resolve(data);
        } catch {
          cleanup();
          resolve(null);
        }
      };
      video.onerror = () => {
        cleanup();
        resolve(null);
      };
    } catch {
      resolve(null);
    }
  });
}

const UploadField = ({
  kind = "image", // "image" | "video" | "audio"
  label,
  hint,
  accept, // input accept attr, e.g. "image/*" or ".mp4,.webm"
  status = "idle", // "idle" | "uploading" | "done" | "error"
  progress = 0, // 0–100 (0 → indeterminate ring)
  previewUrl, // remote URL (image or video poster) — wins over local file
  file, // local File — used for instant preview + meta before upload completes
  fileName,
  fileSize,
  duration, // seconds
  errorMessage,
  maxSizeMB,
  onSelect, // (file) => void
  onRemove, // () => void
  onRetry, // () => void
  disabled = false,
  height = 220,
  multiple = false,
  onSelectMany, // (FileList) => void  (used when multiple)
  sx,
}) => {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [localPreview, setLocalPreview] = useState(null);
  const [localError, setLocalError] = useState("");
  const [imgError, setImgError] = useState(false);

  const accent = ACCENTS[kind] || ACCENTS.image;
  const Icon = ICONS[kind] || ImageIcon;

  // Build a local preview from the selected File (image src or video poster).
  useEffect(() => {
    let revoked = null;
    setLocalPreview(null);
    setImgError(false);
    if (!file) return;
    if (kind === "image") {
      const url = URL.createObjectURL(file);
      revoked = url;
      setLocalPreview(url);
    } else if (kind === "video") {
      captureVideoPoster(file).then((poster) => poster && setLocalPreview(poster));
    }
    return () => {
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [file, kind]);

  const validate = useCallback(
    (f) => {
      if (maxSizeMB && f.size > maxSizeMB * 1024 * 1024) {
        return `Max ${maxSizeMB}MB — ${formatBytes(f.size)} too large`;
      }
      return "";
    },
    [maxSizeMB]
  );

  const emit = useCallback(
    (files) => {
      if (!files || !files.length) return;
      if (multiple) {
        setLocalError("");
        onSelectMany?.(files);
        return;
      }
      const f = files[0];
      const err = validate(f);
      if (err) {
        setLocalError(err);
        return;
      }
      setLocalError("");
      onSelect?.(f);
    },
    [multiple, onSelect, onSelectMany, validate]
  );

  const openPicker = () => {
    if (disabled || status === "uploading") return;
    inputRef.current?.click();
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled || status === "uploading") return;
    emit(e.dataTransfer.files);
  };

  let finalPreviewUrl = previewUrl;
  if (finalPreviewUrl && typeof finalPreviewUrl === "string" && finalPreviewUrl.startsWith("/")) {
    const baseUrl = ENV.API_BASE_URL.replace(/\/api\/?$/, "");
    finalPreviewUrl = `${baseUrl}${finalPreviewUrl}`;
  }
  const preview = finalPreviewUrl || localPreview;
  const shownError = errorMessage || localError;
  const displayName = fileName || file?.name;
  const displaySize = fileSize ?? file?.size;
  const isBusy = status === "uploading";
  const isDone = status === "done";
  const isError = status === "error" || !!localError;

  const accentBorder = `color-mix(in srgb, ${accent} 35%, transparent)`;

  return (
    <Box sx={{ ...sx }}>
      <Box
        onClick={openPicker}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled && !isBusy) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openPicker();
          }
        }}
        sx={{
          position: "relative",
          height,
          borderRadius: "16px",
          overflow: "hidden",
          cursor: disabled || isBusy ? "default" : "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          background: dragOver
            ? `color-mix(in srgb, ${accent} 8%, transparent)`
            : "color-mix(in srgb, var(--text-primary) 2.5%, transparent)",
          border: dragOver
            ? `1.5px solid ${accentBorder}`
            : isError
              ? "1.5px solid rgba(239,68,68,0.4)"
              : isDone || preview
                ? `1.5px solid ${accentBorder}`
                : "1.5px dashed color-mix(in srgb, var(--text-primary) 12%, transparent)",
          transition: "border-color 0.2s, background 0.2s, transform 0.2s",
          opacity: disabled ? 0.5 : 1,
          "&:hover": !disabled && !isBusy && {
            borderColor: isError ? "rgba(239,68,68,0.5)" : accentBorder,
            "& .uf-actions": { opacity: 1 },
          },
          "&:focus-visible": {
            outline: `2px solid color-mix(in srgb, ${accent} 60%, transparent)`,
            outlineOffset: 2,
          },
        }}
      >
        <input
          ref={inputRef}
          type="file"
          hidden
          accept={accept}
          multiple={multiple}
          onChange={(e) => {
            emit(e.target.files);
            e.target.value = "";
          }}
        />

        {/* Preview layer */}
        {preview && (
          <Box sx={{ position: "absolute", inset: 0 }}>
            {kind === "video" && typeof preview === "string" && !preview.startsWith("data:") ? (
              <video 
                src={imgError && localPreview ? localPreview : preview} 
                onError={() => setImgError(true)}
                style={{ width: "100%", height: "100%", objectFit: "cover", filter: isBusy ? "brightness(0.45)" : "none", transition: "filter 0.3s" }} 
                muted 
                playsInline 
              />
            ) : (
              <img 
                src={imgError && localPreview ? localPreview : preview} 
                onError={() => setImgError(true)}
                alt="" 
                style={{ width: "100%", height: "100%", objectFit: "cover", filter: isBusy ? "brightness(0.45)" : "none", transition: "filter 0.3s" }} 
              />
            )}
            {kind === "video" && !isBusy && (
              <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: 54, height: 54, borderRadius: "50%", background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                <PlayArrowIcon sx={{ fontSize: 30, color: "#fff" }} />
              </Box>
            )}
          </Box>
        )}

        {/* IDLE / empty */}
        {!preview && !isBusy && !isError && (
          <Stack alignItems="center" spacing={1.25} sx={{ px: 2, position: "relative", zIndex: 1 }}>
            <Box sx={{ width: 52, height: 52, borderRadius: "14px", display: "flex", alignItems: "center", justifyContent: "center", background: `color-mix(in srgb, ${accent} 12%, transparent)`, border: `1px solid color-mix(in srgb, ${accent} 22%, transparent)` }}>
              {dragOver ? <CloudUploadIcon sx={{ fontSize: 26, color: accent }} /> : <Icon sx={{ fontSize: 26, color: accent }} />}
            </Box>
            <Typography sx={{ color: "var(--text-primary)", fontWeight: 600, fontSize: "0.9rem", fontFamily: "'Inter', sans-serif" }}>
              {dragOver ? "Drop to upload" : label}
            </Typography>
            {hint && (
              <Typography sx={{ color: "color-mix(in srgb, var(--text-primary) 38%, transparent)", fontSize: "0.74rem", fontFamily: "'Inter', sans-serif" }}>
                {hint}
              </Typography>
            )}
            <Typography sx={{ color: "color-mix(in srgb, var(--text-primary) 28%, transparent)", fontSize: "0.7rem", fontFamily: "'Inter', sans-serif" }}>
              Drag &amp; drop or click to browse
            </Typography>
          </Stack>
        )}

        {/* UPLOADING */}
        {isBusy && (
          <Stack alignItems="center" spacing={1.25} sx={{ position: "relative", zIndex: 2 }}>
            <Box sx={{ position: "relative", display: "inline-flex" }}>
              <CircularProgress variant={progress > 0 ? "determinate" : "indeterminate"} value={progress} size={52} thickness={3} sx={{ color: accent }} />
              {progress > 0 && (
                <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--text-primary)", fontFamily: "'Inter', sans-serif" }}>{progress}%</Typography>
                </Box>
              )}
            </Box>
            <Typography sx={{ color: "var(--text-primary)", fontSize: "0.78rem", fontFamily: "'Inter', sans-serif" }}>Uploading…</Typography>
          </Stack>
        )}

        {/* ERROR overlay */}
        {isError && !isBusy && (
          <Stack alignItems="center" spacing={1} sx={{ px: 2, position: "relative", zIndex: 2, background: preview ? "rgba(0,0,0,0.5)" : "transparent", borderRadius: "12px", p: preview ? 2 : 0 }}>
            <ErrorOutlineIcon sx={{ fontSize: 32, color: "#ef4444" }} />
            <Typography sx={{ color: preview ? "#fff" : "#ef4444", fontSize: "0.8rem", fontFamily: "'Inter', sans-serif", fontWeight: 500 }}>
              {shownError || "Upload failed"}
            </Typography>
            {onRetry && status === "error" && (
              <Box
                onClick={(e) => { e.stopPropagation(); onRetry(); }}
                sx={{ mt: 0.5, display: "inline-flex", alignItems: "center", gap: 0.5, px: 1.5, py: 0.5, borderRadius: "8px", background: "color-mix(in srgb, var(--text-primary) 8%, transparent)", border: "1px solid color-mix(in srgb, var(--text-primary) 14%, transparent)", cursor: "pointer", "&:hover": { background: "color-mix(in srgb, var(--text-primary) 14%, transparent)" } }}
              >
                <RefreshIcon sx={{ fontSize: 15, color: "var(--text-primary)" }} />
                <Typography sx={{ fontSize: "0.74rem", color: "var(--text-primary)", fontFamily: "'Inter', sans-serif" }}>Try again</Typography>
              </Box>
            )}
          </Stack>
        )}

        {/* DONE without a visual preview (e.g. video with no poster yet) */}
        {isDone && !preview && !isBusy && !isError && (
          <Stack alignItems="center" spacing={1} sx={{ position: "relative", zIndex: 1, px: 2 }}>
            <Box sx={{ width: 48, height: 48, borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", background: `color-mix(in srgb, ${accent} 12%, transparent)` }}>
              <Icon sx={{ fontSize: 24, color: accent }} />
            </Box>
            <Typography noWrap sx={{ color: "var(--text-primary)", fontWeight: 600, fontSize: "0.85rem", fontFamily: "'Inter', sans-serif", maxWidth: 220 }}>
              {displayName || "Ready"}
            </Typography>
            <Typography sx={{ color: "color-mix(in srgb, var(--text-primary) 38%, transparent)", fontSize: "0.72rem", fontFamily: "'Inter', sans-serif" }}>
              Click to replace
            </Typography>
          </Stack>
        )}

        {/* DONE / preview meta strip + hover actions */}
        {(preview && !isBusy && !isError) && (
          <>
            <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, p: 1.5, background: "linear-gradient(to top, rgba(0,0,0,0.82), transparent)", zIndex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <CheckCircleIcon sx={{ fontSize: 16, color: "#10b981", flexShrink: 0 }} />
                <Typography noWrap sx={{ color: "#fff", fontSize: "0.74rem", fontFamily: "'Inter', sans-serif", fontWeight: 500, flex: 1, minWidth: 0 }}>
                  {displayName || "Ready"}
                </Typography>
                {(displaySize || duration) && (
                  <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.68rem", fontFamily: "'Inter', sans-serif", flexShrink: 0 }}>
                    {[displaySize ? formatBytes(displaySize) : "", duration ? formatClock(duration) : ""].filter(Boolean).join(" · ")}
                  </Typography>
                )}
              </Stack>
            </Box>
            {/* Hover actions */}
            <Stack className="uf-actions" direction="row" spacing={0.75} sx={{ position: "absolute", top: 10, right: 10, opacity: 0, transition: "opacity 0.2s", zIndex: 3 }}>
              <Box onClick={(e) => { e.stopPropagation(); openPicker(); }} title="Replace" sx={{ width: 30, height: 30, borderRadius: "8px", background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", "&:hover": { background: "rgba(0,0,0,0.8)" } }}>
                <RefreshIcon sx={{ fontSize: 16, color: "#fff" }} />
              </Box>
              {onRemove && (
                <Box onClick={(e) => { e.stopPropagation(); onRemove(); }} title="Remove" sx={{ width: 30, height: 30, borderRadius: "8px", background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", "&:hover": { background: "rgba(239,68,68,0.85)" } }}>
                  <CloseIcon sx={{ fontSize: 16, color: "#fff" }} />
                </Box>
              )}
            </Stack>
          </>
        )}
      </Box>
    </Box>
  );
};

export default UploadField;
